import { Fragment, type ReactElement, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Card, Grid, IconButton, Skeleton } from "@mui/material"
import { Avatar, Button, InputText, Menu, MenuItem, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik } from "formik"
import { t } from "i18next"
import moment from "moment"
import { v4 as uuidv4 } from "uuid"

import { KonanAvatar } from "../../components/core/Avatar"
import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { VersionTag } from "../../components/status/VersionTag"
import { getTheme } from "../../hooks/UseTheme"
import { KonanAPI } from "../../services/KonanAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import type { CalculatorCreateRequest } from "../../types/generated/api/CalculatorCreateRequest"
import type { CalculatorEquation } from "../../types/generated/api/CalculatorEquation"
import type { CalculatorGroupRetrieve } from "../../types/generated/api/CalculatorGroupRetrieve"
import type { CalculatorList } from "../../types/generated/api/CalculatorList"
import type { CalculatorVersionRetrieve } from "../../types/generated/api/CalculatorVersionRetrieve"
import type { PatchedCalculatorUpdateRequest } from "../../types/generated/api/PatchedCalculatorUpdateRequest"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import { COMPONENT_TYPES, type SelectedComponent } from "../Predictions/Interfaces"
import { ComponentTestingDialog } from "../Predictions/components/ComponentTestingDialog"
import { EMPTY_EQUATION, FORMIK_INITIAL_VALUES } from "./Constants"
import { ConditionCard } from "./components/ConditionCard"
import { HistoryDialog } from "./components/HistoryDialog"
import { reorderList } from "./helpers"
import type { CalculatorCardProps } from "./interfaces"

import styles from "./CalculatorCard.module.scss"

// Yup validation schema
const validationSchema = Yup.object({
  name: Yup.string().required(t("CalculatorCard.validation.calculatorNameRequired", { ns: "calculators" })),
  equations: Yup.array().of(
    Yup.object({
      equation: Yup.string().required(t("CalculatorCard.validation.equationRequired", { ns: "calculators" })),
      computed_feature_name: Yup.string()
        .required(t("CalculatorCard.validation.equationNameRequired", { ns: "calculators" }))
        .matches(/^[^\s]+$/u, {
          message: t("CalculatorCard.validation.noSpacesOrSpecialChars", { ns: "calculators" }),
        }),
    }),
  ),
})

function CalculatorCard(props: Readonly<CalculatorCardProps>): ReactElement {
  const { uuid, onClose, isDuplicate, onDuplicateClicked, shouldEnableDuplicate, canShowDetails, metaData } = props
  const { id: projectId } = useParams<{ id: string }>()

  const theme = getTheme()

  const { t } = useTranslation("calculators")

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const currentUser = useSelector((state: RootState) => state.auth.email)

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  // Permission check constants
  const canDeleteCalculator = useIsPermitted("Delete Calculator", permissions.konan, flattenedKonanPermissions)
  const canUpdateCalculators = useIsPermitted("Update Calculator", permissions.konan, flattenedKonanPermissions)
  const canCreateCalculators = useIsPermitted("Add Calculator", permissions.konan, flattenedKonanPermissions)

  const [mode, setMode] = useState<"read" | "edit" | "create">(isDuplicate ? "create" : uuid ? "read" : "create")

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)
  const [isVersioningDialogOpen, setIsVersioningDialogOpen] = useState<boolean>(false)
  const [isTestDialogOpen, setIsTestDialogOpen] = useState<boolean>(false)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(menuAnchor)

  const deleteCalculatorMutation = useMutation<
    AxiosResponse,
    AxiosError,
    { project_uuid: string; resource_uuid: string }
  >((props) => KonanAPI.deleteWorkflowResource({ ...props, resource_type: "calculators" }), {
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.calculatorDeleted"), { severity: "success" })

      await queryClient.invalidateQueries("calculators")
    },
  })

  const { isLoading: isCalculatorLoading, data: calculator } = useQuery<
    AxiosResponse<CalculatorGroupRetrieve>,
    AxiosError
  >(
    ["calculator", uuid],
    () =>
      KonanAPI.retrieveCalculator({
        projectId: projectId as string,
        calculatorId: uuid as string,
      }),
    {
      onSuccess: ({ data }) => {
        formik.setValues({
          uuid: data.uuid,
          name: `${data.name}${isDuplicate ? "-copy" : ""}`,
          equations:
            data.active_version?.equations?.map((equation) => ({
              ...equation,
              uuid: uuidv4(),
              equation: equation.equation.slice(1, -1),
            })) ?? [],
        })
      },
      enabled: (!!projectId && uuid !== "new" && canShowDetails) || isDuplicate,
    },
  )

  const createCalculatorMutation = useMutation<
    AxiosResponse,
    AxiosError,
    CalculatorCreateRequest & { projectId: string }
  >(KonanAPI.createCalculator, {
    mutationKey: "componentCreate" + uuid,
    onSuccess: async () => {
      onClose?.()
      await queryClient.invalidateQueries("calculators")
      setMode("read")

      NotificationUtils.toast(t("notifications.calculatorCreated"), {
        severity: "success",
      })
    },
  })

  const updateCalculatorMutation = useMutation<
    AxiosResponse,
    AxiosError,
    PatchedCalculatorUpdateRequest & { projectId: string; calculatorId: string }
  >(KonanAPI.updateCalculator, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["calculator", uuid])
      setMode("read")

      NotificationUtils.toast(t("notifications.calculatorUpdated"), {
        severity: "success",
      })
    },
  })

  const formik = useFormik({
    initialValues: FORMIK_INITIAL_VALUES,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      values.equations = values.equations.map((equation, index) => ({
        ...equation,
        order: index + 1,
      }))

      if (mode === "create")
        await createCalculatorMutation.mutateAsync({
          projectId: projectId as string,
          name: values.name,
          equations: values.equations.map((equation) => ({ ...equation, equation: `(${equation.equation})` })),
        })

      if (mode === "edit")
        await updateCalculatorMutation.mutateAsync({
          projectId: projectId as string,
          calculatorId: uuid as string,
          name: values.name === calculator?.data.name ? undefined : values.name,
          equations: calculator?.data.active_version?.equations?.every(
            (equation, index) =>
              `(${equation.equation})` === values.equations?.[index]?.equation &&
              equation.computed_feature_name === values.equations?.[index]?.computed_feature_name,
          )
            ? undefined
            : values.equations.map((equation) => ({ ...equation, equation: `(${equation.equation})` })),
        })
    },
  })

  const handleCancel = (): void => {
    onClose?.()
    setMode("read")

    formik.resetForm({
      values: {
        ...formik.values,
        name: calculator?.data.name as string,
        equations: calculator?.data.active_version?.equations?.map((equation) => ({
          ...equation,
          equation: equation.equation.slice(1, -1),
        })) as Array<CalculatorEquation & { uuid: string }>,
      },
    })
  }

  const selectedCalculator: SelectedComponent = useMemo(
    () => ({
      id: calculator?.data.uuid ?? "",
      type: COMPONENT_TYPES.CALCULATOR,
      data: metaData as CalculatorList,
    }),
    [calculator?.data, metaData],
  )

  return (
    <Fragment>
      {/* Testing Dialog */}
      {isTestDialogOpen && (
        <ComponentTestingDialog
          isOpen={isTestDialogOpen}
          handleClose={() => setIsTestDialogOpen(false)}
          selectedComponent={selectedCalculator}
        />
      )}

      {isDeletionDialogOpen && (
        <BaseSimpleDialog
          open={isDeletionDialogOpen}
          name={formik.values.name ?? calculator?.data.name}
          onClose={() => setIsDeletionDialogOpen(false)}
          onAccept={() => {
            deleteCalculatorMutation.mutateAsync({
              project_uuid: projectId as string,
              resource_uuid: uuid as string,
            })
          }}
          mode={"calculator-deletion"}
          isLoading={deleteCalculatorMutation.isLoading}
        />
      )}

      {isVersioningDialogOpen && (
        <HistoryDialog
          name={calculator?.data.name as string}
          uuid={uuid}
          activeVersion={calculator?.data.active_version as CalculatorVersionRetrieve}
          onClose={() => setIsVersioningDialogOpen((prev) => !prev)}
        />
      )}

      <Card className={styles.root}>
        <Grid container direction="column" item xs={12} gap={2}>
          <Grid container item xs={12} justifyContent="space-between">
            <Grid item container xs={6} display={"flex"} alignItems={"center"}>
              {isCalculatorLoading && canShowDetails ? (
                <Skeleton width={200} />
              ) : mode !== "read" ? (
                <InputText
                  hideDescription
                  id="name"
                  placeholder={t("calculatorNamePlaceholder")}
                  value={formik.values.name}
                  handleChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name) && formik.errors.name}
                  handleBlur={formik.handleBlur}
                  disabled={formik.isSubmitting}
                  fullWidth
                />
              ) : (
                <Typography variant="h3-bold" textTransform="initial">
                  {formik.values.name.length ? formik.values.name : metaData?.name}
                </Typography>
              )}

              {mode === "read" && metaData?.version && (
                <Grid item ml={1} display={"flex"} alignItems={"center"}>
                  <VersionTag version={metaData?.version} color={"green"} />
                </Grid>
              )}
            </Grid>

            {/* Menu */}
            {mode === "read" && (
              <Grid container item xs justifyContent={"flex-end"}>
                <IconButton
                  aria-label="settings"
                  size="small"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget)}
                >
                  <MoreHorizIcon htmlColor={theme.palette.base.text[1]} />
                </IconButton>

                <Menu
                  key={`calculator-menu`}
                  anchorEl={menuAnchor}
                  open={isMenuOpen}
                  onClose={() => setMenuAnchor(null)}
                  menuMaxContent
                >
                  {canShowDetails && (
                    <MenuItem
                      onClick={() => {
                        navigator.clipboard.writeText(uuid as string)
                        NotificationUtils.toast(t("notifications.copiedToClipboard"), { severity: "success" })
                      }}
                      key={"copy-id-btn"}
                    >
                      {t("copyIdBtn")}
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      setIsTestDialogOpen(true)
                      setMenuAnchor(null)
                    }}
                    className={styles.optionsMenuItem}
                  >
                    <Typography variant="a" color="base" variantColor={2}>
                      {t("testBtn")}
                    </Typography>
                  </MenuItem>
                  {canShowDetails && canDeleteCalculator && (
                    <MenuItem
                      onClick={() => {
                        setIsDeletionDialogOpen(() => true)

                        setMenuAnchor(() => null)
                      }}
                    >
                      <Typography variant="a" color="negative" variantColor={2}>
                        {t("deleteBtn")}
                      </Typography>
                    </MenuItem>
                  )}
                </Menu>
              </Grid>
            )}
          </Grid>

          <Grid
            container
            item
            xs={12}
            justifyContent="space-between"
            sx={{ flexDirection: { xs: "column", md: "row" } }}
          >
            <Avatar
              isLoading={!!canShowDetails && isCalculatorLoading}
              dateTime={metaData?.created_at ? moment(metaData?.created_at) : undefined}
              name={isDuplicate ? currentUser : metaData?.created_by}
              size="regular"
              CustomAvatar={<KonanAvatar size={24} name={isDuplicate ? currentUser : metaData?.created_by} />}
            />

            {/* Action buttons */}
            {canShowDetails && (
              <Grid item>
                {!isCalculatorLoading && mode === "read" ? (
                  <Grid item display={"flex"} gap={1}>
                    {onDuplicateClicked && canCreateCalculators && (
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => {
                          onDuplicateClicked?.()
                        }}
                        disabled={!shouldEnableDuplicate}
                      >
                        {t("duplicateBtn")}
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        setIsVersioningDialogOpen((prev) => !prev)
                      }}
                    >
                      {t("histryBtn")}
                    </Button>
                    {canUpdateCalculators && (
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => {
                          setMode("edit")
                        }}
                      >
                        {t("editBtn")}
                      </Button>
                    )}
                  </Grid>
                ) : (
                  !isCalculatorLoading &&
                  mode !== "read" && (
                    <Grid item display={"flex"} gap={1} alignSelf={"flex-end"}>
                      <Grid item>
                        <Button
                          disabled={isCalculatorLoading}
                          size="small"
                          variant={"secondary"}
                          onClick={handleCancel}
                        >
                          {t("cancelBtn")}
                        </Button>
                      </Grid>

                      <Grid item>
                        <Button
                          variant="primary"
                          size="small"
                          disabled={formik.isSubmitting || isCalculatorLoading || !(formik.dirty && formik.isValid)}
                          onClick={formik.submitForm}
                          isLoading={formik.isSubmitting}
                        >
                          {t("saveBtn")}
                        </Button>
                      </Grid>
                    </Grid>
                  )
                )}
              </Grid>
            )}
          </Grid>

          {canShowDetails || isDuplicate ? (
            <FormikProvider value={formik}>
              {formik.values.equations.map((equation, index) => (
                <ConditionCard
                  key={`${uuid}-${equation.uuid}-${calculator?.data.uuid}`}
                  name={equation.computed_feature_name}
                  equation={equation.equation}
                  setName={(name: string) => formik.setFieldValue(`equations.[${index}].computed_feature_name`, name)}
                  setEquation={(equation: string) => formik.setFieldValue(`equations.[${index}].equation`, equation)}
                  order={index + 1}
                  isReadMode={mode === "read" || formik.isSubmitting}
                  isLastCard={index === formik.values.equations.length - 1}
                  onReorderList={(dir) => {
                    const reorderedEquation = reorderList(formik.values.equations, index, dir)
                    formik.setFieldValue(`equations`, reorderedEquation)
                  }}
                  onDelete={() => formik.setFieldValue(`equations`, formik.values.equations.toSpliced(index, 1))}
                />
              ))}
            </FormikProvider>
          ) : (
            <Grid
              padding={2}
              border={1}
              borderColor={"var(--base-border-1)"}
              bgcolor={"var(--base-background-1)"}
              borderRadius={1}
            >
              <Typography variant="label-regular" color="base">
                {t("hiddenDuePermission")}
              </Typography>
            </Grid>
          )}

          {mode !== "read" && !formik.isSubmitting && (
            <Button
              onClick={() => {
                formik.setFieldValue("equations", [
                  ...formik.values.equations,
                  {
                    uuid: uuidv4(),
                    ...EMPTY_EQUATION,
                    order: formik.values.equations.length + 1,
                  },
                ])
              }}
            >
              {t("addEquationBtn")}
            </Button>
          )}
        </Grid>
      </Card>
    </Fragment>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { validationSchema, CalculatorCard }
